import moment from 'moment';
import type { ApiErrorSingle, ApiV2ServerErrorShape } from '~/api';
import { isGoZeroTime } from '~/utils/date.ts';
import { isVisualRegression } from './env';

export function toTitle(field: string): string {
    if (!field) {
        return '';
    }

    let result = '';
    for (let i = 0; i < field.length; i++) {
        if (field[i] === '_' || field[i] === '-') {
            result += ' ';
            continue;
        }

        if (i > 0 && field[i] === field[i].toUpperCase()) {
            result += ' ';
        }

        if (i === 0) {
            result += field[i].toUpperCase();
        } else {
            result += field[i];
        }
    }
    return result;
}

export function ucfirst(input: any): string {
    return typeof input === 'string' ? input.charAt(0).toUpperCase() + input.slice(1) : '';
}

export function formatNumber(n: any): string {
    return (typeof n === 'number' ? n : Number(n)).toLocaleString('en-US');
}

export function toSingular(name: string): string {
    if (name.endsWith('ies')) {
        return name.substring(0, name.length - 3) + 'y';
    }

    if (name.endsWith('s')) {
        return name.substring(0, name.length - 1);
    }

    return name;
}

export function formatDateTime(
    t: Date | string | number,
    timezone = false,
    seconds = true,
): string {
    if (!timezone && typeof t === 'string' && t.endsWith('Z')) {
        t = t.slice(0, -1);
    }

    const date = new Date(t);
    if (isNaN(date.getTime())) {
        return 'n/a';
    }

    return `${zeroPad(date.getMonth() + 1)}/${zeroPad(
        date.getDate(),
    )}/${date.getFullYear()} ${zeroPad(date.getHours())}:${zeroPad(date.getMinutes())}${
        seconds ? `:${zeroPad(date.getSeconds())}` : ''
    }`;
}

export function formatDate(t: Date | string | number, timezone = false): string {
    if (!t) return '-';
    if (typeof t === 'string') {
        if (isGoZeroTime(t)) return '-';
        if (!timezone && t.endsWith('Z')) {
            t = t.slice(0, -1);
        }
    }
    if (typeof t === 'number') {
        t *= 1000;
    }

    const date = new Date(t);
    if (isNaN(date.getTime())) {
        return '-';
    }

    return `${zeroPad(date.getMonth() + 1)}/${zeroPad(date.getDate())}/${date.getFullYear()}`;
}

export function formatMachineDate(t: Date | string | number, timezone = false): string {
    if (!t) {
        return '';
    }

    if (!timezone && typeof t === 'string' && t.endsWith('Z')) {
        t = t.slice(0, -1);
    } else if (typeof t === 'number') {
        t *= 1000;
    }

    const date = new Date(t);
    if (isNaN(date.getTime())) {
        return '';
    }

    return `${date.getFullYear()}-${zeroPad(date.getMonth() + 1)}-${zeroPad(date.getDate())}`;
}

const CURRENT_YEAR_FORMAT = 'MMM D, h:mma';
const PAST_YEAR_FORMAT = 'MMM D YYYY, h:mma';

export function formatActivityDate(date: Date | string | number): string {
    const d = new Date(date);
    const format =
        d.getFullYear() === new Date().getFullYear() ? CURRENT_YEAR_FORMAT : PAST_YEAR_FORMAT;
    return moment(d).format(format);
}

export function formatLongDate(v: Date | string | number): string {
    const d = new Date(v);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function timeZoneNamePart(
    tz: string,
    timeZoneName: Intl.DateTimeFormatOptions['timeZoneName'],
    date = new Date(),
): string | undefined {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName,
    }).formatToParts(date);
    return parts.find((part) => part.type === 'timeZoneName')?.value;
}

export function formatTimezone(tz: string | undefined, fallback = ''): string {
    if (tz == null || tz === '') {
        return fallback;
    }
    try {
        return timeZoneNamePart(tz, 'short') ?? (fallback || tz);
    } catch {
        return fallback || tz;
    }
}

export function formatTimezoneUtc(tz: string | undefined): string {
    if (tz == null || tz === '') {
        return '';
    }
    try {
        const name = timeZoneNamePart(tz, 'longOffset');
        return name ? name.replace('GMT', 'UTC') : '';
    } catch {
        return '';
    }
}

export function zeroPad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
}

export function roundTo(value: number, decimals: number): number {
    if (!Number.isFinite(value)) return 0;
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function removeLeadingZeros(v: string): string {
    return v.replace(/^0+(?=\d)/, '');
}

const moneyFormats: Record<string, Intl.NumberFormat> = {
    USD: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    }),
    USD_long: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    }),
    USD_round: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }),
    GBP: new Intl.NumberFormat('en-UK', {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    }),
    EUR: new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    }),
};

export function formatMoney(amount: number, format: string = 'USD', trim = false): string {
    if (!moneyFormats[format]) {
        format = 'USD';
    }

    const result = moneyFormats[format].format(amount);
    if (trim) {
        return result.replace('.00', '');
    }
    return result;
}

export function formatMoneyShort(amount: number, currency?: string): string {
    const options: Intl.NumberFormatOptions = {
        notation: 'compact',
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
    };

    switch (currency) {
        case undefined:
            break;
        case 'USD':
        case 'GBP':
        case 'EUR':
            options.style = 'currency';
            options.currency = currency;
            break;
        default:
            console.warn(`unknown curency "${currency}"`);
    }

    const moneyShort = Intl.NumberFormat('en', options);

    return `${moneyShort.format(amount)}`;
}

export function formatRelativeDays(days: number): string {
    if (isVisualRegression()) {
        return `2137 days`;
    }
    if (days >= 365) {
        const years = round(days / 365);
        return `${years} year${years !== 1 ? 's' : ''}`;
    } else if (days >= 30) {
        const months = Math.floor(days / 30);
        return `${months} month${months !== 1 ? 's' : ''}`;
    } else {
        return `${days} day${days !== 1 ? 's' : ''}`;
    }
}

function round(n: number): number {
    const num = Number(n.toFixed(1));
    if (Math.ceil(Number(num)) === Math.floor(Number(num))) {
        return Math.floor(num);
    }
    return num;
}

// HEADS UP: this helper is intended to work with month values from 1 to 12.
// Avoid sending a javascript Date.getMonth()
export function monthName(n: number, format: string = 'MMMM'): string {
    return moment()
        .month(n - 1)
        .format(format);
}

type RelativeTimeInterval = {
    short: string;
    long: string;
    seconds: number;
};

const intervals: RelativeTimeInterval[] = [
    { short: 'y', long: 'year', seconds: 31536000 },
    { short: 'mo', long: 'month', seconds: 2592000 },
    { short: 'd', long: 'day', seconds: 86400 },
    { short: 'h', long: 'hour', seconds: 3600 },
    { short: 'm', long: 'minute', seconds: 60 },
    { short: 's', long: 'second', seconds: 1 },
];

type RelativeTimeOptions = {
    format: 'long' | 'short';
    prefix: boolean;
    suffix: boolean;
    pluralize: boolean;
    separator: string;
};

const DEFAULT_OPT_RELATIVE_TIME: RelativeTimeOptions = {
    format: 'long',
    prefix: true,
    suffix: true,
    pluralize: true,
    separator: ' ',
};

export function relativeTime(
    date: Date | string | number,
    opt?: Record<string, any> | null,
): string {
    if (isVisualRegression()) {
        return `2137 days`;
    }
    let d: Date;
    if (typeof date === 'string' || typeof date === 'number') {
        d = new Date(date);
    } else {
        d = date;
    }
    const { format, prefix, suffix, pluralize, separator } = {
        ...DEFAULT_OPT_RELATIVE_TIME,
        ...opt,
    };

    const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
    if (seconds < 1 && seconds >= 0) {
        return 'now';
    }

    const isFuture = seconds < 0;
    const absSeconds = Math.abs(seconds);
    const interval = intervals.find((i) => i.seconds <= absSeconds)!;
    if (interval === undefined) return '';

    const count = Math.floor(absSeconds / interval.seconds);
    return `${prefix && isFuture ? 'in ' : ''}${count}${separator}${interval[format]}${
        pluralize && count !== 1 ? 's' : ''
    }${suffix && !isFuture ? ' ago' : ''}`;
}

// intervalSeconds transform a number of seconds into its human representation.
// Example: 90 => 1m30s
export function intervalSeconds(seconds: number): string {
    const resp = [];
    for (const interval of intervals) {
        const qty = Math.trunc(seconds / interval.seconds);
        if (qty > 0) {
            resp.push(`${qty}${interval.short}`);
        }
        seconds = seconds % interval.seconds;
        if (seconds === 0) {
            break;
        }
    }
    return resp.join('');
}

export function formatCallDuration(seconds: number): string {
    if (!seconds || seconds <= 0) {
        return '-';
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
        if (minutes > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${hours}h`;
    }

    if (minutes > 0) {
        return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
    }

    return `${remainingSeconds}s`;
}

export type CountryCode = keyof typeof countries;
export const countries: Record<string, string> = {
    AF: 'Afghanistan',
    AX: 'Åland Islands',
    AL: 'Albania',
    DZ: 'Algeria',
    AS: 'American Samoa',
    AD: 'Andorra',
    AO: 'Angola',
    AI: 'Anguilla',
    AQ: 'Antarctica',
    AG: 'Antigua and Barbuda',
    AR: 'Argentina',
    AM: 'Armenia',
    AW: 'Aruba',
    AU: 'Australia',
    AT: 'Austria',
    AZ: 'Azerbaijan',
    BS: 'Bahamas',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BB: 'Barbados',
    BY: 'Belarus',
    BE: 'Belgium',
    BZ: 'Belize',
    BJ: 'Benin',
    BM: 'Bermuda',
    BT: 'Bhutan',
    BO: 'Bolivia',
    BQ: 'Bonaire, Sint Eustatius and Saba',
    BA: 'Bosnia and Herzegovina',
    BW: 'Botswana',
    BV: 'Bouvet Island',
    BR: 'Brazil',
    IO: 'British Indian Ocean Territory',
    BN: 'Brunei Darussalam',
    BG: 'Bulgaria',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    CV: 'Cabo Verde',
    KH: 'Cambodia',
    CM: 'Cameroon',
    CA: 'Canada',
    KY: 'Cayman Islands',
    CF: 'Central African Republic',
    TD: 'Chad',
    CL: 'Chile',
    CN: 'China',
    CX: 'Christmas Island',
    CC: 'Cocos (Keeling) Islands',
    CO: 'Colombia',
    KM: 'Comoros',
    CG: 'Congo',
    CD: 'Democratic Republic of the Congo',
    CK: 'Cook Islands',
    CR: 'Costa Rica',
    CI: "Côte d'Ivoire",
    HR: 'Croatia',
    CU: 'Cuba',
    CW: 'Curaçao',
    CY: 'Cyprus',
    CZ: 'Czechia',
    DK: 'Denmark',
    DJ: 'Djibouti',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    EC: 'Ecuador',
    EG: 'Egypt',
    SV: 'El Salvador',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    EE: 'Estonia',
    SZ: 'Eswatini',
    ET: 'Ethiopia',
    FK: 'Falkland Islands (Malvinas)',
    FO: 'Faroe Islands',
    FJ: 'Fiji',
    FI: 'Finland',
    FR: 'France',
    GF: 'French Guiana',
    PF: 'French Polynesia',
    TF: 'French Southern Territories',
    GA: 'Gabon',
    GM: 'Gambia',
    GE: 'Georgia',
    DE: 'Germany',
    GH: 'Ghana',
    GI: 'Gibraltar',
    GR: 'Greece',
    GL: 'Greenland',
    GD: 'Grenada',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GT: 'Guatemala',
    GG: 'Guernsey',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    GY: 'Guyana',
    HT: 'Haiti',
    HM: 'Heard Island and McDonald Islands',
    VA: 'Holy See',
    HN: 'Honduras',
    HK: 'Hong Kong',
    HU: 'Hungary',
    IS: 'Iceland',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran',
    IQ: 'Iraq',
    IE: 'Ireland',
    IM: 'Isle of Man',
    IL: 'Israel',
    IT: 'Italy',
    JM: 'Jamaica',
    JP: 'Japan',
    JE: 'Jersey',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KE: 'Kenya',
    KI: 'Kiribati',
    KP: 'North Korea',
    KR: 'South Korea',
    XK: 'Kosovo',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: "Lao People's Democratic Republic",
    LV: 'Latvia',
    LB: 'Lebanon',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libya',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MO: 'Macao',
    MG: 'Madagascar',
    MW: 'Malawi',
    MY: 'Malaysia',
    MV: 'Maldives',
    ML: 'Mali',
    MT: 'Malta',
    MH: 'Marshall Islands',
    MQ: 'Martinique',
    MR: 'Mauritania',
    MU: 'Mauritius',
    YT: 'Mayotte',
    MX: 'Mexico',
    FM: 'Micronesia',
    MD: 'Moldova',
    MC: 'Monaco',
    MN: 'Mongolia',
    ME: 'Montenegro',
    MS: 'Montserrat',
    MA: 'Morocco',
    MZ: 'Mozambique',
    MM: 'Myanmar',
    NA: 'Namibia',
    NR: 'Nauru',
    NP: 'Nepal',
    NL: 'Netherlands',
    NC: 'New Caledonia',
    NZ: 'New Zealand',
    NI: 'Nicaragua',
    NE: 'Niger',
    NG: 'Nigeria',
    NU: 'Niue',
    NF: 'Norfolk Island',
    MK: 'North Macedonia',
    MP: 'Northern Mariana Islands',
    NO: 'Norway',
    OM: 'Oman',
    PK: 'Pakistan',
    PW: 'Palau',
    PS: 'Palestine',
    PA: 'Panama',
    PG: 'Papua New Guinea',
    PY: 'Paraguay',
    PE: 'Peru',
    PH: 'Philippines',
    PN: 'Pitcairn',
    PL: 'Poland',
    PT: 'Portugal',
    PR: 'Puerto Rico',
    QA: 'Qatar',
    RE: 'Réunion',
    RO: 'Romania',
    RU: 'Russian Federation',
    RW: 'Rwanda',
    BL: 'Saint Barthélemy',
    SH: 'Saint Helena, Ascension and Tristan da Cunha',
    KN: 'Saint Kitts and Nevis',
    LC: 'Saint Lucia',
    MF: 'Saint Martin (French part)',
    PM: 'Saint Pierre and Miquelon',
    VC: 'Saint Vincent and the Grenadines',
    WS: 'Samoa',
    SM: 'San Marino',
    ST: 'Sao Tome and Principe',
    SA: 'Saudi Arabia',
    SN: 'Senegal',
    RS: 'Serbia',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SG: 'Singapore',
    SX: 'Sint Maarten (Dutch part)',
    SK: 'Slovakia',
    SI: 'Slovenia',
    SB: 'Solomon Islands',
    SO: 'Somalia',
    ZA: 'South Africa',
    GS: 'South Georgia and the South Sandwich Islands',
    SS: 'South Sudan',
    ES: 'Spain',
    LK: 'Sri Lanka',
    SD: 'Sudan',
    SR: 'Suriname',
    SJ: 'Svalbard and Jan Mayen',
    SE: 'Sweden',
    CH: 'Switzerland',
    SY: 'Syrian Arab Republic',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TZ: 'Tanzania',
    TH: 'Thailand',
    TL: 'Timor-Leste',
    TG: 'Togo',
    TK: 'Tokelau',
    TO: 'Tonga',
    TT: 'Trinidad and Tobago',
    TN: 'Tunisia',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    TC: 'Turks and Caicos Islands',
    TV: 'Tuvalu',
    UG: 'Uganda',
    UA: 'Ukraine',
    AE: 'United Arab Emirates',
    GB: 'United Kingdom',
    US: 'United States',
    UM: 'United States Minor Outlying Islands',
    UY: 'Uruguay',
    UZ: 'Uzbekistan',
    VU: 'Vanuatu',
    VE: 'Venezuela',
    VN: 'Viet Nam',
    VG: 'Virgin Islands (British)',
    VI: 'Virgin Islands (U.S.)',
    WF: 'Wallis and Futuna',
    EH: 'Western Sahara',
    YE: 'Yemen',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',
} as const;

export const flags: Record<string, string> = {
    AD: '🇦🇩',
    AE: '🇦🇪',
    AF: '🇦🇫',
    AG: '🇦🇬',
    AI: '🇦🇮',
    AL: '🇦🇱',
    AM: '🇦🇲',
    AN: '🇧🇶',
    AO: '🇦🇴',
    AQ: '🇦🇶',
    AR: '🇦🇷',
    AS: '🇦🇸',
    AT: '🇦🇹',
    AU: '🇦🇺',
    AW: '🇦🇼',
    AX: '🇦🇽',
    AZ: '🇦🇿',
    BA: '🇧🇦',
    BB: '🇧🇧',
    BD: '🇧🇩',
    BE: '🇧🇪',
    BF: '🇧🇫',
    BG: '🇧🇬',
    BH: '🇧🇭',
    BI: '🇧🇮',
    BJ: '🇧🇯',
    BL: '🇧🇱',
    BM: '🇧🇲',
    BN: '🇧🇳',
    BO: '🇧🇴',
    BQ: '🇧🇶',
    BR: '🇧🇷',
    BS: '🇧🇸',
    BT: '🇧🇹',
    BV: '🇧🇻',
    BW: '🇧🇼',
    BY: '🇧🇾',
    BZ: '🇧🇿',
    CA: '🇨🇦',
    CC: '🇨🇨',
    CD: '🇨🇩',
    CF: '🇨🇫',
    CG: '🇨🇬',
    CH: '🇨🇭',
    CI: '🇨🇮',
    CK: '🇨🇰',
    CL: '🇨🇱',
    CM: '🇨🇲',
    CN: '🇨🇳',
    CO: '🇨🇴',
    CR: '🇨🇷',
    CU: '🇨🇺',
    CV: '🇨🇻',
    CW: '🇨🇼',
    CX: '🇨🇽',
    CY: '🇨🇾',
    CZ: '🇨🇿',
    DE: '🇩🇪',
    DJ: '🇩🇯',
    DK: '🇩🇰',
    DM: '🇩🇲',
    DO: '🇩🇴',
    DZ: '🇩🇿',
    EC: '🇪🇨',
    EE: '🇪🇪',
    EG: '🇪🇬',
    EH: '🇪🇭',
    ER: '🇪🇷',
    ES: '🇪🇸',
    ET: '🇪🇹',
    FI: '🇫🇮',
    FJ: '🇫🇯',
    FK: '🇫🇰',
    FM: '🇫🇲',
    FO: '🇫🇴',
    FR: '🇫🇷',
    GA: '🇬🇦',
    GB: '🇬🇧',
    GD: '🇬🇩',
    GE: '🇬🇪',
    GF: '🇬🇫',
    GG: '🇬🇬',
    GH: '🇬🇭',
    GI: '🇬🇮',
    GL: '🇬🇱',
    GM: '🇬🇲',
    GN: '🇬🇳',
    GP: '🇬🇵',
    GQ: '🇬🇶',
    GR: '🇬🇷',
    GS: '🇬🇸',
    GT: '🇬🇹',
    GU: '🇬🇺',
    GW: '🇬🇼',
    GY: '🇬🇾',
    HK: '🇭🇰',
    HM: '🇭🇲',
    HN: '🇭🇳',
    HR: '🇭🇷',
    HT: '🇭🇹',
    HU: '🇭🇺',
    ID: '🇮🇩',
    IE: '🇮🇪',
    IL: '🇮🇱',
    IM: '🇮🇲',
    IN: '🇮🇳',
    IO: '🇮🇴',
    IQ: '🇮🇶',
    IR: '🇮🇷',
    IS: '🇮🇸',
    IT: '🇮🇹',
    JE: '🇯🇪',
    JM: '🇯🇲',
    JO: '🇯🇴',
    JP: '🇯🇵',
    KE: '🇰🇪',
    KG: '🇰🇬',
    KH: '🇰🇭',
    KI: '🇰🇮',
    KM: '🇰🇲',
    KN: '🇰🇳',
    KP: '🇰🇵',
    KR: '🇰🇷',
    KW: '🇰🇼',
    KY: '🇰🇾',
    KZ: '🇰🇿',
    LA: '🇱🇦',
    LB: '🇱🇧',
    LC: '🇱🇨',
    LI: '🇱🇮',
    LK: '🇱🇰',
    LR: '🇱🇷',
    LS: '🇱🇸',
    LT: '🇱🇹',
    LU: '🇱🇺',
    LV: '🇱🇻',
    LY: '🇱🇾',
    MA: '🇲🇦',
    MC: '🇲🇨',
    MD: '🇲🇩',
    ME: '🇲🇪',
    MF: '🇲🇫',
    MG: '🇲🇬',
    MH: '🇲🇭',
    MK: '🇲🇰',
    ML: '🇲🇱',
    MM: '🇲🇲',
    MN: '🇲🇳',
    MO: '🇲🇴',
    MP: '🇲🇵',
    MQ: '🇲🇶',
    MR: '🇲🇷',
    MS: '🇲🇸',
    MT: '🇲🇹',
    MU: '🇲🇺',
    MV: '🇲🇻',
    MW: '🇲🇼',
    MX: '🇲🇽',
    MY: '🇲🇾',
    MZ: '🇲🇿',
    NA: '🇳🇦',
    NC: '🇳🇨',
    NE: '🇳🇪',
    NF: '🇳🇫',
    NG: '🇳🇬',
    NI: '🇳🇮',
    NL: '🇳🇱',
    NO: '🇳🇴',
    NP: '🇳🇵',
    NR: '🇳🇷',
    NU: '🇳🇺',
    NZ: '🇳🇿',
    OM: '🇴🇲',
    PA: '🇵🇦',
    PE: '🇵🇪',
    PF: '🇵🇫',
    PG: '🇵🇬',
    PH: '🇵🇭',
    PK: '🇵🇰',
    PL: '🇵🇱',
    PM: '🇵🇲',
    PN: '🇵🇳',
    PR: '🇵🇷',
    PS: '🇵🇸',
    PT: '🇵🇹',
    PW: '🇵🇼',
    PY: '🇵🇾',
    QA: '🇶🇦',
    RE: '🇷🇪',
    RO: '🇷🇴',
    RS: '🇷🇸',
    RU: '🇷🇺',
    RW: '🇷🇼',
    SA: '🇸🇦',
    SB: '🇸🇧',
    SC: '🇸🇨',
    SD: '🇸🇩',
    SE: '🇸🇪',
    SG: '🇸🇬',
    SH: '🇸🇭',
    SI: '🇸🇮',
    SJ: '🇸🇯',
    SK: '🇸🇰',
    SL: '🇸🇱',
    SM: '🇸🇲',
    SN: '🇸🇳',
    SO: '🇸🇴',
    SR: '🇸🇷',
    SS: '🇸🇸',
    ST: '🇸🇹',
    SV: '🇸🇻',
    SX: '🇸🇽',
    SY: '🇸🇾',
    SZ: '🇸🇿',
    TC: '🇹🇨',
    TD: '🇹🇩',
    TF: '🇹🇫',
    TG: '🇹🇬',
    TH: '🇹🇭',
    TJ: '🇹🇯',
    TK: '🇹🇰',
    TL: '🇹🇱',
    TM: '🇹🇲',
    TN: '🇹🇳',
    TO: '🇹🇴',
    TR: '🇹🇷',
    TT: '🇹🇹',
    TV: '🇹🇻',
    TW: '🇹🇼',
    TZ: '🇹🇿',
    UA: '🇺🇦',
    UG: '🇺🇬',
    UM: '🇺🇲',
    US: '🇺🇸',
    UY: '🇺🇾',
    UZ: '🇺🇿',
    VA: '🇻🇦',
    VC: '🇻🇨',
    VE: '🇻🇪',
    VG: '🇻🇬',
    VI: '🇻🇮',
    VN: '🇻🇳',
    VU: '🇻🇺',
    WF: '🇼🇫',
    WS: '🇼🇸',
    XK: '🇽🇰',
    YE: '🇾🇪',
    YT: '🇾🇹',
    ZA: '🇿🇦',
    ZM: '🇿🇲',
    ZW: '🇿🇼',
};

export const latam: CountryCode[] = [
    'AR',
    'BO',
    'BR',
    'BZ',
    'CL',
    'CO',
    'CR',
    'DO',
    'EC',
    'GF',
    'GT',
    'GY',
    'HN',
    'HT',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SR',
    'SV',
    'UY',
];

export const COUNTRY_OPTIONS = Object.keys(countries).map((k) => ({
    label: countries[k],
    value: k,
}));

export const usaStates: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    PR: 'Puerto Rico',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

export const canadaStates: Record<string, string> = {
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NT: 'Northwest Territories',
    NS: 'Nova Scotia',
    NU: 'Nunavut',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    YT: 'Yukon',
};

export const bannedCountries: string[] = ['BY', 'CN', 'CU', 'IR', 'KP', 'SY', 'VE'];

export function formatAddressRegion(region: string = '-', country: string = ''): string {
    if (country === 'US') {
        return usaStates[region] || region;
    }
    if (country === 'CA') {
        return canadaStates[region] || region;
    }
    return region;
}

export function trimData(data: any) {
    if (typeof data === 'object') {
        for (const k of Object.keys(data as Record<string, any>)) {
            if (typeof data[k] === 'string') {
                data[k] = data[k].trim();
            }
        }
    }
}

export function formatSalary(
    start: number | string | null | undefined,
    end: number | string | null | undefined,
    currency: string,
): string {
    const endSalary = !Number(end) ? null : Number(end);
    const startSalary = !Number(start) ? null : Number(start);
    if (!endSalary && !startSalary) return '';

    if (!endSalary) {
        return `From ${formatMoney(startSalary!, currency)}`;
    }

    if (!startSalary) {
        return `Up to ${formatMoney(endSalary, currency)}`;
    }

    return `${formatMoney(startSalary, currency)} - ${formatMoney(endSalary, currency)}`;
}

export function formatDuration(duration: number): string {
    const result = [];
    if (duration >= 60) {
        result.push(`${Math.floor(duration / 60)}h`);
        duration %= 60;
    }

    if (duration > 0) {
        result.push(`${duration} min`);
    }

    return result.join(' ');
}

export function fullName(
    user: { first_name: string; last_name: string } | null | undefined,
): string {
    if (!user?.first_name && !user?.last_name) return '';
    return `${user.first_name} ${user.last_name}`.trim();
}

export function formatCsv(header: string[], rows: Record<string, any>[]): string {
    return header.join(',') + '\n' + rows.map((r) => r.map(quoteCsvCol).join(',')).join('\n');
}

function quoteCsvCol(col: any): string {
    if (col === null || col === undefined || col === 'null') return '""';
    return `"${typeof col === 'string' ? col.replaceAll('"', '""') : col}"`;
}

export function cleanPhone(raw: string): string {
    const regex = /( |\)|\(|\+-)/g;
    return raw.replace(regex, '').replace('+', '').replace('-', '');
}

export function whatsAppUrl(phone: string): string {
    return `https://api.whatsapp.com/send?phone=${cleanPhone(phone)}`;
}

export function formatDateEng(input: string, { showYear = true, utc = false } = {}): string {
    return parseDate(input, utc).format(showYear ? 'MMM Do YYYY' : 'MMM Do');
}

export function formatDateEngWithTimezone(
    input: string,
    { showYear = true, utc = false, timezone = '' } = {},
): string {
    timezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    let tz = moment.tz(input, timezone).format('z');
    if (!isNaN(parseFloat(tz))) {
        tz = timezone;
    }
    const calendar = parseDate(input, utc)
        .tz(timezone)
        .format(`MMM DD,${showYear ? ' YYYY ' : ' '}h:mm A`);
    return `${calendar} (${tz})`;
}

export function formatCalendarDate(input: string, utc = false): string {
    return parseDate(input, utc).format('MMM DD, YYYY h:mm A');
}

export function formatFullDate(input: string, utc = false): string {
    return parseDate(input, utc).format('MMM DD, YYYY');
}

function parseDate(input: moment.MomentInput, utc = false): moment.Moment {
    return (utc ? moment.utc : moment)(input);
}

export function formatError(error: any): string {
    if (Array.isArray(error)) {
        return error
            .map(formatError)
            .filter((x) => x)
            .join('. ');
    }

    let msg;
    if (typeof error === 'object') {
        msg = error['message'] as string;
    } else {
        msg = `${error}`;
    }
    return msg?.split('validation error: ').pop() || '';
}

type Error = {
    code?: number;
    message?: string;
    internal?: string;
};

export function parseError(errMsg: ApiErrorSingle): Error {
    if (errMsg instanceof Response) {
        return {
            code: errMsg.status,
            message: errMsg.statusText,
            internal: errMsg.statusText,
        };
    }

    if (typeof errMsg === 'object' && Object.hasOwn(errMsg, 'message')) {
        return {
            message: (errMsg as { message: string }).message,
        };
    }

    if (typeof errMsg === 'object' && errMsg !== null) {
        const { status, detail, errors } = errMsg as ApiV2ServerErrorShape;
        const message = (errors && formatError(errors)) || detail;
        if (message) {
            return { code: status, message };
        }
    }

    const { groups: { code, message, internal } = {} } =
        /^(?:code=(?<code>\d{3}), )?(?:message=)?(?<message>.+?)(?:, internal=(?<internal>.+?))?$/.exec(
            String(errMsg),
        ) || {};

    return {
        code: parseInt(code) || undefined,
        message: message ? ucfirst(message) : undefined,
        internal: internal ? ucfirst(internal) : undefined,
    };
}

export function deletedAtError(
    error: any,
    table: string,
    value: number | number[],
): string | undefined {
    const match = formatError(error).match(/(?:deleted at (?<date>[\d/]+) )/);
    const date = match?.['groups']?.['date'];
    if (!date) {
        return undefined;
    }

    const ids = [value].flat().join(',');

    return `${table}#${ids} deleted at ${formatDate(date)}`;
}

export const MONTH_OPTIONS: { label: string; value: number }[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
];

export const DAY_OPTIONS: { label: string; value: number }[][] = [
    31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
].map((l) => Array.from(Array(l)).map((_, i) => ({ value: i + 1, label: `${i + 1}` })));

export function pluralize(value: number, one: any, many: any): any {
    return value === 1 ? one : many;
}

export function formatDurationMonths(months: number): string | null {
    if (!Number.isFinite(months) || months < 1) {
        return null;
    }
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const parts: string[] = [];
    if (years > 0) {
        parts.push(`${years} ${pluralize(years, 'year', 'years')}`);
    }
    if (remainingMonths > 0) {
        parts.push(`${remainingMonths} ${pluralize(remainingMonths, 'month', 'months')}`);
    }
    return parts.length ? parts.join(', ') : null;
}

export function joinWords(words: string[]): string {
    return [words.slice(0, -1).join(', '), words.slice(-1)].filter((a) => a).join(' and ');
}

export function relativeTimeOrEmpty(input: Date | number | string): string {
    if (!input) return '';
    const date = new Date(input);
    return relativeTime(date, { prefix: false, suffix: false });
}

export function daysSince(date: Date | number | string, from = new Date()): number {
    if (isVisualRegression() || !date) return 0;
    return moment(from).diff(moment(date), 'days');
}

export function formatLinkedInUrl(url: string): string {
    return `linkedin.com/${url.split('linkedin.com/').pop()!.split('?')[0]}`;
}

export function timeOptions(intervalMinutes: number): { value: string; label: string }[] {
    const t = moment('2024-01-01 00:00:00');
    const res: { value: string; label: string }[] = [];
    for (let i = 0; ; i++) {
        const tt = t.clone().add(i * intervalMinutes, 'minutes');
        if (tt.day() === 2) {
            break;
        }
        const elem = tt.format('h:mma');
        res.push({ value: elem, label: elem });
    }
    return res;
}

export const INFINITE_CHAR = '∞';

export function formatStateName(country: string, region: string) {
    if (country === 'US') {
        return usaStates[Object.keys(usaStates).find((s) => s === region) ?? ''];
    }
    if (country === 'CA') {
        return canadaStates[Object.keys(canadaStates).find((s) => s === region) ?? ''];
    }
    return region ?? '-';
}
